import React, { useCallback, useEffect, useState } from 'react';
import { IVaultPasswordPolicy, TranslationsTypes } from '@app/models';
import { PageActions, PasslyTextField } from '@app/components';
import { PasslyAutoComplete } from '@app/componentsV2';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { IVaultItem, IVaultItemInfo } from '@app/pages/passwordManager/interfaces';
import { getDefaultPolicy, getDisplayNameError } from '@app/pages/passwordManager/helpers';
import { getLoaderBtn } from '@app/store/actions';
import { vaultsServiceNew } from '@app/services';
import { genericErrorToast } from '@app/helpers';
import { AxiosError, AxiosResponse } from 'axios';

interface IProps {
    vaultInfo: IVaultItemInfo;
    allVaults: IVaultItem[];
    passwordPolicies: IVaultPasswordPolicy[];
    setVaultInfo: (vaultInfo: (prevVaultInfo: IVaultItemInfo | null) => any) => void;
    changeVaultNameAfterEdit: (vaultId: string, newDisplayName: string) => void;
}

const VaultInformation: React.SFC<IProps> = ({ vaultInfo, passwordPolicies, allVaults, setVaultInfo, changeVaultNameAfterEdit }) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [displayName, setDisplayName] = useState('');
    const [description, setDescription] = useState('');
    const [displayNameError, setDisplayNameError] = useState('');
    const [passwordPolicy, setPasswordPolicy] = useState<string>('');
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    useEffect(() => {
        setIsSubmitDisabled(!displayName || !!displayNameError);
    }, [displayName, displayNameError]);

    useEffect(() => {
        setPasswordPolicy(getDefaultPolicy(passwordPolicies));
    }, [passwordPolicies]);

    useEffect(() => {
        reset();
    }, [vaultInfo]);

    const reset = () => {
        setDisplayName(vaultInfo.displayName);
        setDescription(vaultInfo.description || '');
        setPasswordPolicy(vaultInfo.passwordPolicyId);
    };

    const onFormChange = useCallback(
        (e: any) => {
            const { name, value } = e.target;

            if (name === 'vaultName') {
                const error: TranslationsTypes = getDisplayNameError(value, allVaults);

                setDisplayName(value);
                setDisplayNameError(error ? translation(error) : '');
            } else if (name === 'description') {
                setDescription(value);
            } else if (name === 'passwordPolicy') {
                setPasswordPolicy(value.id);
            }
        },
        [allVaults]
    );

    const handleSuccess = (response: AxiosResponse) => {
        const newVaultData = response.data;

        setVaultInfo((prevVaultInfo) => ({ ...prevVaultInfo, ...newVaultData }));
        changeVaultNameAfterEdit(vaultInfo.id, newVaultData.displayName);
        dispatch(getLoaderBtn(false));
    };

    const handleError = (error: AxiosError<any>) => {
        genericErrorToast(error);

        dispatch(getLoaderBtn(false));
    };

    const handleSubmit = () => {
        dispatch(getLoaderBtn(true));

        const jsonObject = {
            displayName: displayName,
            description: description,
            passwordPolicyId: passwordPolicy
        };

        vaultsServiceNew.editVault(jsonObject, vaultInfo.id, handleError, handleSuccess);
    };

    const handleCancel = () => {
        reset();
    };

    return (
        <div className='vaults__info-info-tab'>
            <div className='form__row'>
                <PasslyTextField
                    name='vaultName'
                    label={'VAULT_NAME'}
                    value={displayName}
                    onChange={onFormChange}
                    id='vaultName-input'
                    inputProps={{ maxLength: 255 }}
                    autoFocus
                    required
                    fullWidth
                    error={!!displayNameError}
                    helperText={displayNameError}
                />
            </div>
            <div className='form__row'>
                <PasslyTextField
                    fullWidth
                    multiline
                    minRows={4}
                    label={translation('DESCRIPTION')}
                    id='description-input'
                    name='description'
                    onChange={onFormChange}
                    type='text'
                    value={description}
                    inputProps={{ maxLength: 500 }}
                />
            </div>

            {passwordPolicies?.length && (
                <div className='form__row'>
                    <PasslyAutoComplete
                        items={passwordPolicies}
                        onChange={onFormChange}
                        id='passwordPolicy-input'
                        name='passwordPolicy'
                        label={translation('VAULT_PASSWORD_POLICY')}
                        value={passwordPolicy}
                        valueField='id'
                        field='displayName'
                        dropdown={true}
                        appendTo={document.body}
                    />
                </div>
            )}

            {/*<div className='form__row'>
                <PasslyAutoComplete
                    items={availableOrgs}
                    onChange={handleSelectDataChange}
                    id='organization-input'
                    name='organization'
                    label={translation('ORGANIZATION')}
                    value={editData.organization}
                    dropdown={true}
                />
            </div>*/}
            <PageActions
                primaryBtn={translation('SAVE')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmit}
                disabled={isSubmitDisabled}
                onClickSecondary={handleCancel}
                justify='start'
            />
        </div>
    );
};

export default VaultInformation;
